/**
 * useResources
 * ============
 * React Query replacement for the `fetchResources` async thunk and the
 * `resourceAdapter` entity adapter in commonSlice.
 *
 * Fetches resources for every resource group in every subscription and returns
 * a single flat array of `Resource` objects — the same shape the old Redux
 * `resourceSelectors.selectAll` returned.
 *
 * Derived hooks:
 *   - `useSites()` — resources filtered to `Cozify.Sites`
 *   - `useDeviceRegisterResources()` — resources filtered to `Cozify.DeviceRegister`
 *
 * The old `useSitesAndDeviceRegisters` hook is superseded by this module.
 * Its `SiteInfo` and `DeviceRegisterInfo` types are re-exported here for
 * backward compatibility.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useSubscriptions } from './useSubscriptions';
import { useAllResourceGroups } from './useResourceGroups';
import { queryKeys } from '~services/queryKeys';
import { handleQueryError } from '~services/queryUtils';
import { resourcesProvider } from '~services/providers';
import type { Resource } from '~types/commonTypes';

// Re-export the convenience types that useTenantDevices imports.
export type SiteInfo = {
  name: string;
  path: string;
  siteId: string;
};

export type DeviceRegisterInfo = {
  id: string;
  name: string;
};

function parseResourceProperties(properties: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) => {
      try {
        return [key, JSON.parse(value)];
      } catch {
        return [key, value];
      }
    })
  );
}

/**
 * Core query function: given an array of resource groups, fetch all resources
 * for each one and return a flat `Resource[]`.
 */
async function fetchAllResources(
  resourceGroups: { id: string; subscriptionId: string; name: string }[]
): Promise<Resource[]> {
  const results = await Promise.allSettled(
    resourceGroups.map(async rg => {
      const entries = await resourcesProvider.listResources(
        rg.subscriptionId,
        rg.name
      );
      return entries.map(
        r =>
          ({
            ...r,
            properties: parseResourceProperties(r.properties),
            resourceGroupId: rg.id,
            resourceType: r.resourceProvider,
          }) as unknown as Resource
      );
    })
  );
  return results.flatMap(r => (r.status === 'fulfilled' ? r.value : []));
}

async function fetchResourceGroupResources(
  subscriptionId: string,
  resourceGroup: string
): Promise<Resource[]> {
  const entries = await resourcesProvider.listResources(
    subscriptionId,
    resourceGroup
  );
  const resourceGroupId = `/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}`;
  return entries.map(
    resource =>
      ({
        ...resource,
        properties: parseResourceProperties(resource.properties),
        resourceGroupId,
        resourceType: resource.resourceProvider,
      }) as unknown as Resource
  );
}

/**
 * Canonical hook: fetches ALL resources across all subscriptions & resource
 * groups and returns them as a flat `Resource[]`.
 *
 * The query is automatically enabled once subscriptions and resource groups
 * have been loaded.
 */
export function useResources(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const { data: subscriptions = [] } = useSubscriptions();
  const subscriptionIds = useMemo(
    () => (enabled ? subscriptions.map(s => s.subscriptionId) : []),
    [enabled, subscriptions]
  );
  const { data: resourceGroups = [], isFetched: fetchedResourceGroups } =
    useAllResourceGroups(subscriptionIds);

  return useQuery({
    queryKey: queryKeys.resources.list(resourceGroups.map(rg => rg.id).sort()),
    queryFn: () => fetchAllResources(resourceGroups),
    enabled: enabled && fetchedResourceGroups && resourceGroups.length > 0,
    staleTime: 1000 * 60 * 10,
    meta: { onError: handleQueryError },
  });
}

export function useResourceGroupResources(
  subscriptionId: string | undefined,
  resourceGroup: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.resources.list({ subscriptionId, resourceGroup }),
    queryFn: () => fetchResourceGroupResources(subscriptionId!, resourceGroup!),
    enabled: !!subscriptionId && !!resourceGroup,
    staleTime: 1000 * 60 * 10,
    meta: { onError: handleQueryError },
  });
}

/**
 * Derived hook: returns only `Cozify.Sites` resources.
 */
export function useSites() {
  const { data: resources, ...rest } = useResources();
  const sites = useMemo(
    () => resources?.filter(r => r.resourceType === 'Cozify.Sites') ?? [],
    [resources]
  );
  return { ...rest, data: sites };
}

/**
 * Derived hook: returns only `Cozify.DeviceRegister` resources.
 */
export function useDeviceRegisterResources() {
  const { data: resources, ...rest } = useResources();
  const registers = useMemo(
    () =>
      resources?.filter(r => r.resourceType === 'Cozify.DeviceRegister') ?? [],
    [resources]
  );
  return { ...rest, data: registers };
}

/**
 * Drop-in replacement for the old `useSitesAndDeviceRegisters` hook.
 * Returns `{ sites: SiteInfo[], deviceRegisters: DeviceRegisterInfo[] }`.
 */
export function useSitesAndDeviceRegisters() {
  const { data: resources, ...rest } = useResources();
  const { data: subscriptions = [] } = useSubscriptions();
  const subscriptionIds = useMemo(
    () => subscriptions.map(s => s.subscriptionId),
    [subscriptions]
  );
  const { data: resourceGroups = [] } = useAllResourceGroups(subscriptionIds);

  const derived = useMemo(() => {
    if (!resources)
      return {
        sites: [] as SiteInfo[],
        deviceRegisters: [] as DeviceRegisterInfo[],
      };

    const sites: SiteInfo[] = resources
      .filter(r => r.resourceType === 'Cozify.Sites')
      .map(r => {
        const rg = resourceGroups.find(rg => rg.id === r.resourceGroupId);
        const sub = subscriptions.find(
          s => s.subscriptionId === rg?.subscriptionId
        );
        return {
          name: r.name,
          path: `/sites?type=resource&siteId=${r.name}&id=${sub?.id}/resourceGroups/${rg?.name}/providers/Cozify.Sites/${r.name}`,
          siteId: r.id,
        };
      });

    const deviceRegisters: DeviceRegisterInfo[] = resources
      .filter(r => r.resourceType === 'Cozify.DeviceRegister')
      .map(r => ({ id: r.id, name: r.name }));

    return { sites, deviceRegisters };
  }, [resources, resourceGroups, subscriptions]);

  return { ...rest, data: derived };
}
